import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { ForumFilterParametersParams as ForumFilterParametersProps } from "messaging/forums/_api/queries/_schemas/forum-filter-schema";
import { ForumOrderParametersProps } from "messaging/forums/_api/queries/_schemas/forum-order-schema";
import ForumQuerySchema from "messaging/forums/_api/queries/_schemas/forum-query-schema";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import ForumName from "notifications/values/forum-name/forum-name";

export type ForumQueryParams = {
  filterParameters: ForumFilterParameters;
  orderParameters: ForumOrderParameters;
  paginationParameters?: PaginationParameters;
  query?: string;
};

export default class ForumQuery {
  public static fromJSON(json: unknown): ForumQuery | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const validated = ForumQuerySchema.validateJSON(json);
    const parsed = ForumQuerySchema.deserialize(validated);
    return new ForumQuery(parsed);
  }
  public readonly filterParameters: ForumFilterParameters;
  public readonly orderParameters: ForumOrderParameters;
  public readonly paginationParameters?: PaginationParameters;

  public constructor(params: ForumQueryParams) {
    this.filterParameters = params.filterParameters;
    this.orderParameters = params.orderParameters;
    this.paginationParameters = params.paginationParameters;
  }

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.filterParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...(this.paginationParameters?.searchParams ?? []),
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  public isEqualTo(other: unknown): boolean {
    if (!(other instanceof ForumQuery)) return false;
    return Boolean(
      this.filterParameters.isEqualTo(other.filterParameters) &&
        this.orderParameters.isEqualTo(other.orderParameters) &&
        ((!this.paginationParameters && !other.paginationParameters) ||
          this.paginationParameters?.isEqualTo(other.paginationParameters))
    );
  }
}

export class ForumFilterParameters {
  public name?: ForumName;
  public entityId?: Guid;
  public entityClass?: ForumEntityClass;
  public context?: string;
  public subscriberId?: Guid;
  public minCreatedDate?: AHDate;
  public maxCreatedDate?: AHDate;
  public systemOnly?: AHBoolean;
  public forumId?: Guid;
  public query?: string;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams(asKeyValueArray(this));

    if (this.name) searchParams.append("name", this.name.toString());
    if (this.entityId) searchParams.append("entityId", this.entityId.toString());
    if (this.entityClass) searchParams.append("entityClass", this.entityClass.toString());
    if (this.context) searchParams.append("context", this.context);
    if (this.subscriberId) searchParams.append("subscriberId", this.subscriberId.toString());
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.systemOnly) searchParams.append("systemOnly", this.systemOnly.toString());
    if (this.forumId) searchParams.append("forumId", this.forumId.toString());
    if (this.query) searchParams.append("query", this.query);

    return searchParams;
  }

  public constructor(params: ForumFilterParametersProps = {}) {
    this.name = params.name;
    this.entityId = params.entityId;
    this.entityClass = params.entityClass;
    this.context = params.context;
    this.subscriberId = params.subscriberId;
    this.minCreatedDate = params.minCreatedDate;
    this.maxCreatedDate = params.maxCreatedDate;
    this.systemOnly = params.systemOnly;
    this.forumId = params.forumId;
    this.query = params.query;
  }

  public isEqualTo(other: ForumFilterParameters): boolean {
    if (!other) return false;
    return Boolean(
      this.name === other.name &&
        this.entityId === other.entityId &&
        this.entityClass === other.entityClass &&
        this.context === other.context &&
        this.subscriberId === other.subscriberId &&
        this.minCreatedDate?.toISOString() === other.minCreatedDate?.toISOString() &&
        this.maxCreatedDate?.toISOString() === other.maxCreatedDate?.toISOString() &&
        this.systemOnly === other.systemOnly &&
        this.forumId === other.forumId &&
        this.query === other.query
    );
  }
}

export class ForumOrderParameters {
  public readonly orderBy?: ForumOrderField;
  public readonly orderDir?: OrderDirection;

  public constructor(params: ForumOrderParametersProps = {}) {
    this.orderBy = params.orderBy ? ForumOrderField[params?.orderBy as keyof typeof ForumOrderField] : undefined;
    this.orderDir = params.orderDir ? OrderDirection[params?.orderDir as keyof typeof OrderDirection] : undefined;
  }

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.orderBy) searchParams.append("orderBy", this.orderBy);
    if (this.orderDir) searchParams.append("orderDir", this.orderDir);
    return searchParams;
  }

  public isEqualTo(other: ForumOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }
}

export enum ForumOrderField {
  CreatedDate = "CreatedDate",
  Name = "Name",
  EntityId = "EntityId",
  EntityClass = "EntityClass",
  Context = "Context",
}

function asKeyValueArray(params: ForumFilterParameters): [string, string][] {
  return Object.entries(params).filter(([, value]) => !!value && typeof value === "string") as [string, string][];
}
